import { useQuery } from "@tanstack/react-query"
import ResponseError from "../../../types/responseError"
import { CarrotPlanEmployeeSettings } from "#/types/carrotPlanTypes"
import { carrotClient } from "#/utils/CarrotClient"
import buildCarrotApiRetryPolicy from "../../../utils/CarrotApiRetryPolicy"
import useQueryExpirationHandler from "../../context/authentication/hooks/useQueryExpirationHandler"
import { useMemo } from "react"

export const carrotPlanEmployeeSettingsQueryKey = "GetCarrotPlanEmployeeSettings"

export default function useCarrotPlanEmployeeSettings() {
  const carrotPlanEmployeeSettingsQuery = useQuery<CarrotPlanEmployeeSettings, ResponseError>(
    [carrotPlanEmployeeSettingsQueryKey],
    () => carrotClient.getCarrotPlanEmployeeSettings(),
    {
      retry: buildCarrotApiRetryPolicy(3)
    }
  )

  const isQueryReady = useMemo(
    () =>
      !carrotPlanEmployeeSettingsQuery.isError &&
      !carrotPlanEmployeeSettingsQuery.isLoading &&
      !!carrotPlanEmployeeSettingsQuery.data,
    [
      carrotPlanEmployeeSettingsQuery.isError,
      carrotPlanEmployeeSettingsQuery.isLoading,
      carrotPlanEmployeeSettingsQuery.data
    ]
  )

  const isDoneForNow = useMemo(
    () => isQueryReady && !!carrotPlanEmployeeSettingsQuery.data.doneForNowLockedAtUtc,
    [isQueryReady, carrotPlanEmployeeSettingsQuery.data]
  )

  const memberRejectedCarrotPlan = useMemo(
    () => isQueryReady && !!carrotPlanEmployeeSettingsQuery.data.memberRejectedCarrotPlan,
    [isQueryReady, carrotPlanEmployeeSettingsQuery.data]
  )

  const appIsUnlockedAndPendingCarrotPlan = useMemo(
    () => isQueryReady && !!carrotPlanEmployeeSettingsQuery.data.unlockedAndPendingCarrotPlan,
    [isQueryReady, carrotPlanEmployeeSettingsQuery.data]
  )

  const isSensitiveState = useMemo(
    () => isQueryReady && !!carrotPlanEmployeeSettingsQuery.data.isSensitiveState,
    [isQueryReady, carrotPlanEmployeeSettingsQuery.data]
  )

  useQueryExpirationHandler(carrotPlanEmployeeSettingsQuery)
  return {
    ...carrotPlanEmployeeSettingsQuery,
    isDoneForNow,
    memberRejectedCarrotPlan,
    appIsUnlockedAndPendingCarrotPlan,
    isSensitiveState
  }
}
